import React, { useCallback, useEffect, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { getProviderAutocomplete, getProviderId } from 'features/salesOrder/services';
import { tariffProviderNotAssignedId } from 'pages/SalesOrder/SalesOrderScreen/constants';

type ProviderOption = {
	id: string;
	fantasyName: string;
	businessName: string;
	stateName: string;
};

interface Props {
	value: string;
	onChange: (providerId: string) => void;
	label?: string;
	required?: boolean;
	error?: boolean;
	helperText?: string;
}

export const WizardProviderAutocomplete = ({
	value,
	onChange,
	label = 'Proveedor de tarifas',
	required = true,
	error = false,
	helperText,
}: Props): JSX.Element => {
	const [selected, setSelected] = useState<ProviderOption | null>(null);
	const [options, setOptions] = useState<ProviderOption[]>([]);
	const [inputValue, setInputValue] = useState('');
	const debouncer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!value || value === 'n/a') {
			setSelected(null);
			return;
		}
		let cancelled = false;
		getProviderId(value)
			.then((result) => {
				if (cancelled || !result.data) {
					return;
				}
				const opt: ProviderOption = {
					id: String(result.data.id),
					fantasyName: result.data.fantasyName,
					businessName: result.data.businessName,
					stateName: result.data.stateName,
				};
				setSelected(opt);
				setInputValue(`${opt.fantasyName} (${opt.businessName})`);
			})
			.catch(() => {
				if (!cancelled && value === tariffProviderNotAssignedId) {
					setInputValue('Proveedor de cotización (sin asignar en orden)');
				}
			});
		return () => {
			cancelled = true;
		};
	}, [value]);

	const searchProviders = useCallback(async (query: string) => {
		if (query.length < 4) {
			setOptions([]);
			return;
		}
		try {
			const response = await getProviderAutocomplete(query);
			setOptions(
				response.data.map((option) => ({
					id: String(option.id),
					fantasyName: option.fantasyName,
					businessName: option.businessName,
					stateName: option.stateName,
				})),
			);
		} catch {
			setOptions([]);
		}
	}, []);

	const handleInputChange = (_: unknown, newInput: string) => {
		setInputValue(newInput);
		if (debouncer.current) {
			clearTimeout(debouncer.current);
		}
		debouncer.current = setTimeout(() => {
			void searchProviders(newInput);
		}, 300);
	};

	return (
		<Autocomplete
			options={options}
			value={selected}
			inputValue={inputValue}
			onInputChange={handleInputChange}
			getOptionLabel={(opt) => (opt ? `${opt.fantasyName} (${opt.businessName})` : '')}
			isOptionEqualToValue={(a, b) => a.id === b.id}
			onChange={(_, newValue) => {
				setSelected(newValue);
				onChange(newValue?.id || '');
				if (newValue) {
					setInputValue(`${newValue.fantasyName} (${newValue.businessName})`);
				}
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					required={required}
					size="small"
					error={error}
					helperText={helperText || 'Buscá al menos 4 caracteres'}
					fullWidth
				/>
			)}
		/>
	);
};
